import { useQuery } from "@tanstack/react-query"
import {
	formatRubles,
	type InvoiceSpec,
	type KeyboardButton,
	type MediaRef,
} from "@velizhanin-dev/bot-scenario"
import { CheckCheck, Copy, CreditCard, ExternalLink, Paperclip } from "lucide-react"
import { toast } from "sonner"
import { getScenarioMediaUrl, TelegramHtml, type TestChatEntry } from "@/entities/bot-builder"
import { MEDIA_KIND_LABELS } from "@/shared/config/media"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { StatusBadge } from "@/shared/ui/status-badge"
import { formatTime, INLINE_BUTTON_CLASS, STYLE_CLASSES, TG } from "../lib/telegram-theme"

function Time({ at, read }: { at: string; read?: boolean }) {
	return (
		<span className="mt-0.5 flex items-center justify-end gap-0.5 text-[11px] leading-none text-white/45">
			{formatTime(at)}
			{read ? <CheckCheck className="h-3.5 w-3.5 text-sky-300" /> : null}
		</span>
	)
}

function MediaPreview({ scenarioId, media }: { scenarioId: string; media: MediaRef }) {
	const s3Key = media.s3Key
	const url = useQuery({
		queryKey: ["bot-builder", "media-url", scenarioId, s3Key],
		queryFn: () => getScenarioMediaUrl(scenarioId, s3Key as string),
		enabled: !!s3Key && (media.kind === "photo" || media.kind === "sticker"),
		staleTime: 30 * 60_000,
	})
	if ((media.kind === "photo" || media.kind === "sticker") && url.data) {
		return <img src={url.data.url} alt="" className="max-h-56 w-full rounded-xl object-cover" />
	}
	return (
		<div className="flex items-center gap-2.5 rounded-xl bg-white/5 px-2.5 py-2 text-xs">
			<span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-sky-500/80">
				<Paperclip className="h-4 w-4" />
			</span>
			<span className="min-w-0">
				<span className="block truncate">{media.fileName}</span>
				<span className="block text-white/50">{MEDIA_KIND_LABELS[media.kind]}</span>
			</span>
		</div>
	)
}

function InlineButton({
	button,
	disabled,
	onPress,
}: {
	button: KeyboardButton
	disabled: boolean
	onPress: (callbackData: string) => void
}) {
	switch (button.kind) {
		case "callback":
			return (
				<Button
					type="button"
					variant="ghost"
					disabled={disabled}
					onClick={() => onPress(button.data)}
					className={cn(INLINE_BUTTON_CLASS, "h-auto", button.style && STYLE_CLASSES[button.style])}
				>
					<span className="truncate">{button.label}</span>
				</Button>
			)
		case "copy":
			return (
				<Button
					type="button"
					variant="ghost"
					title={button.text}
					className={cn(INLINE_BUTTON_CLASS, "h-auto", button.style && STYLE_CLASSES[button.style])}
					onClick={() => {
						void navigator.clipboard?.writeText(button.text)
						toast.success(`Скопировано: ${button.text}`)
					}}
				>
					<span className="truncate">{button.label}</span>
					<Copy className="h-3 w-3 shrink-0 opacity-70" />
				</Button>
			)
		default:
			return (
				<a
					href={button.url}
					target="_blank"
					rel="noreferrer noopener"
					title={button.kind === "web_app" ? "Кнопка мини-аппа" : button.url}
					className={cn(INLINE_BUTTON_CLASS, button.style && STYLE_CLASSES[button.style])}
				>
					<span className="truncate">{button.label}</span>
					<ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
				</a>
			)
	}
}

/** Счёт Telegram Payments: в тест-чате оплата имитируется, деньги не списываются. */
function InvoiceCard({
	invoice,
	at,
	disabled,
	onPay,
	onShow,
}: {
	invoice: InvoiceSpec
	at: string
	disabled: boolean
	onPay: () => void
	onShow: () => void
}) {
	const price = formatRubles(invoice.amount / 100)
	const asks = [
		invoice.needName && "имя",
		invoice.needPhone && "телефон",
		invoice.needEmail && "email",
	].filter(Boolean)
	return (
		<div className="w-72 max-w-[88%] space-y-1">
			<div className={cn("overflow-hidden rounded-2xl rounded-bl-md text-white", TG.incoming)}>
				{invoice.photoUrl ? (
					<img src={invoice.photoUrl} alt="" className="h-36 w-full object-cover" />
				) : null}
				<Button
					type="button"
					variant="ghost"
					title="Показать блок на холсте"
					onClick={onShow}
					className="h-auto w-full flex-col items-start justify-start space-y-1 rounded-none px-3 py-2 text-left hover:bg-transparent"
				>
					<p className="text-[14px] leading-snug font-semibold">{invoice.title}</p>
					{invoice.description ? (
						<p className="text-[13px] leading-snug whitespace-pre-wrap text-white/75">
							{invoice.description}
						</p>
					) : null}
					<div className="flex items-center gap-2 pt-1">
						<span className="text-[15px] font-semibold">{price}</span>
						<StatusBadge tone="warning" size="tag">
							тест
						</StatusBadge>
					</div>
					{asks.length > 0 || invoice.receipt ? (
						<p className="text-[11px] text-white/45">
							{asks.length > 0 ? `Спросит ${asks.join(", ")}` : ""}
							{asks.length > 0 && invoice.receipt ? " · " : ""}
							{invoice.receipt ? "чек по 54-ФЗ" : ""}
						</p>
					) : null}
					<Time at={at} />
				</Button>
			</div>
			<Button
				type="button"
				variant="ghost"
				disabled={disabled}
				onClick={onPay}
				className={cn(INLINE_BUTTON_CLASS, "h-auto")}
			>
				<CreditCard className="h-4 w-4" />
				Оплатить {price}
			</Button>
		</div>
	)
}

export function Entry({
	entry,
	scenarioId,
	disabled,
	onPress,
	onPay,
	onShowNode,
}: {
	entry: TestChatEntry
	scenarioId: string
	disabled: boolean
	onPress: (callbackData: string) => void
	onPay: (nodeId: string) => void
	onShowNode: (nodeId: string) => void
}) {
	if (entry.from === "system") {
		return (
			<div className="flex justify-center py-1">
				<p className="max-w-[90%] rounded-xl bg-black/35 px-3 py-1 text-center text-xs whitespace-pre-wrap text-white/75">
					{entry.text}
				</p>
			</div>
		)
	}
	if (entry.from === "user") {
		return (
			<div className="flex justify-end">
				<div
					className={cn(
						"max-w-[80%] rounded-2xl rounded-br-md px-3 py-1.5 text-[14px] leading-snug text-white shadow-sm",
						TG.outgoing,
					)}
				>
					<p className="break-words whitespace-pre-wrap">{entry.text}</p>
					<Time at={entry.at} read />
				</div>
			</div>
		)
	}
	if (entry.invoice) {
		return (
			<InvoiceCard
				invoice={entry.invoice}
				at={entry.at}
				disabled={disabled}
				onPay={() => onPay(entry.nodeId)}
				onShow={() => onShowNode(entry.nodeId)}
			/>
		)
	}
	const hasBody = !!entry.text || !!entry.media || !!entry.gallery?.length
	if (!hasBody && !entry.keyboard?.length) return null
	return (
		<div className="max-w-[88%] space-y-1">
			{hasBody ? (
				<Button
					type="button"
					variant="ghost"
					title="Показать блок на холсте"
					onClick={() => onShowNode(entry.nodeId)}
					className={cn(
						"h-auto w-full flex-col items-start justify-start space-y-1.5 rounded-2xl rounded-bl-md px-3 py-1.5 text-left text-[14px] leading-snug text-white shadow-sm hover:bg-transparent hover:text-white hover:ring-1 hover:ring-sky-400/40",
						TG.incoming,
					)}
				>
					{entry.gallery?.length ? (
						<div className="-mx-1.5 mt-0.5 grid grid-cols-2 gap-1">
							{entry.gallery.map((media, index) => (
								<MediaPreview
									key={media.s3Key ?? media.telegram?.fileId ?? index}
									scenarioId={scenarioId}
									media={media}
								/>
							))}
						</div>
					) : entry.media ? (
						<div className="-mx-1.5 mt-0.5">
							<MediaPreview scenarioId={scenarioId} media={entry.media} />
						</div>
					) : null}
					{entry.text ? (
						<div className="break-words whitespace-pre-wrap">
							<TelegramHtml html={entry.text} />
						</div>
					) : null}
					<Time at={entry.at} />
				</Button>
			) : null}
			{entry.keyboard?.length ? (
				<div className="space-y-1">
					{entry.keyboard.map((row, rowIndex) => (
						<div key={rowIndex} className="flex gap-1">
							{row.map((button, index) => (
								<InlineButton key={index} button={button} disabled={disabled} onPress={onPress} />
							))}
						</div>
					))}
				</div>
			) : null}
		</div>
	)
}
